/** Data + hora opcional para formulários; helpers para serializar em ISO. */
import type { ChangeEvent } from 'react';
import Input from './Input';
import { cn } from '@/lib/utils';

export type DateTimeParts = { date: string; time: string };

const EMPTY_PARTS: DateTimeParts = { date: '', time: '' };

/** Converte ISO (ou Date) em `yyyy-MM-dd` e `HH:mm` para inputs nativos. */
export function parseDateTimeParts(value?: string | Date | null): DateTimeParts {
  if (!value) return { ...EMPTY_PARTS };
  const d = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(d.getTime())) return { ...EMPTY_PARTS };

  const date = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  const h = d.getHours();
  const m = d.getMinutes();
  const time = h !== 0 || m !== 0 ? `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}` : '';

  return { date, time };
}

/** Monta ISO a partir de partes; sem hora usa meio-dia local (padrão de vencimento). */
export function buildDateTimeIso(
  date: string,
  time?: string,
  defaultHour = 12,
  defaultMinute = 0,
): string | undefined {
  if (!date) return undefined;
  const [year, month, day] = date.split('-').map(Number);
  if (time) {
    const [hours, minutes] = time.split(':').map(Number);
    return new Date(year, month - 1, day, hours, minutes, 0, 0).toISOString();
  }
  return new Date(year, month - 1, day, defaultHour, defaultMinute, 0, 0).toISOString();
}

export interface DateTimeFieldProps {
  date: string;
  time: string;
  onChange: (parts: DateTimeParts) => void;
  dateLabel?: string;
  timeLabel?: string;
  dateName?: string;
  timeName?: string;
  disabled?: boolean;
  error?: string;
  className?: string;
  /** Rótulo acessível do grupo (fieldset); padrão: data + hora. */
  groupLabel?: string;
}

export default function DateTimeField({
  date,
  time,
  onChange,
  dateLabel = 'Data',
  timeLabel = 'Horário',
  dateName = 'date',
  timeName = 'time',
  disabled,
  error,
  className,
  groupLabel,
}: DateTimeFieldProps) {
  const handleDate = (e: ChangeEvent<HTMLInputElement>) => {
    onChange({ date: e.target.value, time });
  };

  const handleTime = (e: ChangeEvent<HTMLInputElement>) => {
    onChange({ date, time: e.target.value });
  };

  const legendText = groupLabel ?? `${dateLabel} e ${timeLabel}`;

  return (
    <fieldset
      className={cn(
        'grid min-w-0 grid-cols-1 gap-4 border-0 p-0 m-0',
        'sm:grid-cols-2 sm:gap-3',
        className,
      )}
      disabled={disabled}
      aria-invalid={error ? true : undefined}
    >
      <legend className="sr-only">{legendText}</legend>
      <Input
        label={dateLabel}
        type="date"
        name={dateName}
        value={date}
        onChange={handleDate}
        disabled={disabled}
        error={error}
      />
      <Input
        label={timeLabel}
        type="time"
        name={timeName}
        value={time}
        onChange={handleTime}
        disabled={disabled}
      />
    </fieldset>
  );
}
